import { ThreadModel, ThreadService, CommentService } from '@anecdotes/commenting';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { groupBy, toDictionary } from '@anecdotes/utils';
import { combineLatest, NEVER, merge, distinctUntilKeyChanged, map } from 'rxjs';
import { delay, filter, mergeMap, switchMap, tap } from 'rxjs/operators';
import { CommentFilterOptionDefinition, ThreadViewModel } from '../../../models';
import { createComposedResourceId } from '../../../utils';
import {
  ClearStateAfterClosingAction,
  CloseAction,
  DisplaySingleThreadAction,
  SetResourceViewModelsAction
} from '../../actions';
import {
  selectIsInitialized,
  selectResources,
  selectSelectedFilterOption,
  selectThreadCreationFor,
} from '../../selectors';
import { AuthService, RoleEnum, RoleService, UserClaims } from '@anecdotes/auth-core';
import { CommentingFacadeService } from '../../../services';
import { UserFacadeService } from '@anecdotes/identity';

@Injectable()
export class CommentingPanelEffects {
  constructor(
    private store: Store,
    private threadService: ThreadService,
    private commentService: CommentService,
    private authService: AuthService,
    private actions$: Actions,
    private commentingFacadeService: CommentingFacadeService,
    private userFacade: UserFacadeService,
    private roleService: RoleService,
  ) {}

  threadViewModels$ = createEffect(() => this.store.select(selectIsInitialized).pipe(
    switchMap((isInitialized) => {
      if (isInitialized) {
        return combineLatest([
          this.store.select(selectResources),
          this.threadService.getAll(),
          this.store.select(selectThreadCreationFor),
          this.commentService.getAll(),
          this.authService.getUserAsync(),
          this.userFacade.getUsers(),
          this.roleService.isAuditor()
        ]).pipe(
          tap(([resources, threads, resourceToCreateThreadFor, comments, currentUser, allUsers, isAuditor]) => {
            const dictionary = toDictionary(
              groupBy(threads, (thread) => createComposedResourceId(thread.resourceType, thread.resourceId)),
              (group) => group.key,
              (group) => group.values
            );

            const allAuditorUsers = allUsers.filter((user) => user.role === RoleEnum.Auditor);
            const allAuditorUsersIds = allAuditorUsers.map((user) => user.uid);

            const threadViewModels: ThreadViewModel[] = resources
              .map((resource) => {
                const currentTrackingId = createComposedResourceId(resource.resourceType, resource.resourceId);
                const threads: ThreadModel[] = dictionary[currentTrackingId];
                let result: ThreadViewModel[] = [];

                if (threads) {
                  result = threads.map((thread) => {
                    const threadComments = comments.filter((com) => com.threadId === thread.id);

                    const isThreadMentioned =
                      this.isUserWasMentioned(currentUser, thread.body) ||
                      threadComments.some((comment) => this.isUserWasMentioned(currentUser, comment.body));

                    const someOfAuditorsIsInitiator = allAuditorUsersIds.includes(thread.initiatorId);
                    const someOfAuditorsIsMentioned = allAuditorUsers.some(
                      (user) =>
                        this.isUserWasMentioned(user, thread.body) ||
                          threadComments.some((comment) => this.isUserWasMentioned(user, comment.body)),
                    );

                    let recentActivityAt = thread.createdAt;

                    if (threadComments?.length) {
                      recentActivityAt = threadComments.reduce((comment1, comment2) =>
                        comment1?.createdAt > comment2?.createdAt ? comment1 : comment2
                      )?.createdAt;
                    }

                    if (
                      isAuditor &&
                      !someOfAuditorsIsInitiator &&
                      !someOfAuditorsIsMentioned
                    ) {
                      return;
                    }

                    return {
                      ...resource,
                      threadState: thread.state,
                      threadId: thread.id,
                      isThreadMentioned,
                      createdAt: thread.createdAt,
                      recentActivityAt,
                      body: thread.body,
                      comments: threadComments,
                      initiatorId: thread.initiatorId,
                    } as ThreadViewModel;
                  }).filter((thread) => !!thread);
                }

                // With this we can display thread creation placeholder in accordance with order of resources
                // It should append the placeholder after all available threads for a resource
                if (
                  resourceToCreateThreadFor &&
                  currentTrackingId ===
                  createComposedResourceId(
                    resourceToCreateThreadFor.resourceType,
                    resourceToCreateThreadFor.resourceId
                  )
                ) {
                  result = [
                    ...result,
                    {
                      ...resourceToCreateThreadFor,
                      threadId: undefined,
                      isCreation: true,
                      comments: [],
                    },
                  ];
                }

                return result;
              })
              .reduce((result, array) => [...result, ...array], []);

            this.store.dispatch(SetResourceViewModelsAction({ threadViewModels }));
          })
        );
      }

      return NEVER;
    })
  ), { dispatch: false });

  selectFilterOption$ = createEffect(() => {
    const selectSingleThread$ = this.actions$.pipe(
      ofType(DisplaySingleThreadAction),
      distinctUntilKeyChanged('resourceId'),
    );
    const filterOptionChanges$ = this.store.select(selectSelectedFilterOption).pipe(filter(Boolean));

    return merge(selectSingleThread$, filterOptionChanges$)
      .pipe(
        switchMap(() => filterOptionChanges$),
        filter(Boolean),
        mergeMap(({ value }: CommentFilterOptionDefinition) => this.commentingFacadeService.trackPanelFiltering(value)),
      );
  }, { dispatch: false });

  clearStateAfterPanelClosing$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CloseAction),
      delay(1000),
      map(() => ClearStateAfterClosingAction()),
    );
  });

  private isUserWasMentioned(currentUser: UserClaims, messageBody: string): boolean {
    return messageBody?.includes(`${currentUser.first_name} ${currentUser.last_name}`);
  }
}
